import { reactive } from 'vue'
import { createClient, AuthType} from 'webdav'

import { WDocument, WSection, WScene } from '@/include/models.js'
import l10n from '@/include/l10n.js'

var Store = reactive({
	servers: null,
	dirty: false,
	list: [],
	trackEdit: {},
	sectionEdit: null,
	sceneEdit: null,
	drawer: false,

	meta: null,
	doc: null,
	docKey: "wr-doc",

	initialize: false,
	messages: reactive([]),

	langs: null,
	l: null,

	settings: null,
	settingsKey: "wr-settings",

	exception: null,

	emptyMeta() {
		return {
			serverUUID: null,
			serverPath: null,
			serverNonce: null,
			serverDirty: false,
		};
	},

	init() {

		if (location.hash === "#reset") {
			localStorage.removeItem(this.docKey);
			localStorage.removeItem(this.settingsKey);
		}

		// Settings
		let settings = localStorage.getItem(this.settingsKey);

		this.settings = Object.assign(
			{
				lang: "en",
				servers: [],
				theme: "auto",
			},
			(settings ? JSON.parse(settings) : {})
		);

		this.settings.servers = new Map(this.settings.servers);

		// Langs
		this.langs = l10n.langs;
		this.l = l10n[this.settings.lang];

		// Statics
		for (let i in WDocument.trackTypes) WDocument.trackTypes[i].label = this.l[WDocument.trackTypes[i].label];
		WScene.new_label = this.l.new_scene;
		for (let i in WScene.states) WScene.states[i].label = this.l[WScene.states[i].label];
		WSection.new_label = this.l.new_chapter;

		// Doc
		let data = localStorage.getItem(this.docKey);

		if (data) {
			data = JSON.parse(data);

			// Back compatibility
			if (!data.meta) {
				data = {
					doc: data,
					meta: this.emptyMeta(),
				};
			}

			this.meta = (data.meta || this.emptyMeta());

			try {
				this.setDoc(new WDocument(data.doc, this.l));
			} catch (err) {
				this.docNew();
			}
		} else {
			this.docNew();
		}

		if (this.meta.serverUUID && !this.settings.servers.has(this.meta.serverUUID)) {
			this.meta.serverUUID = null;
		}

		this.docCheck();

		setInterval(() => {
			if (this.dirty) {
				this.docFlush();
				this.dirty = false;
			}
		}, 5000);

	},

	pushMessage(text, type = "success") {
		let ts = new Date;
		ts.setSeconds(ts.getSeconds() + 5);
		this.messages.push({ text, type, ts });
	},

	removeMessage(i) {
		this.messages.splice(i, 1);
	},

	cron() {
		let ts = new Date();
		while (this.messages.length && this.messages[0].ts < ts) this.messages.shift();
	},

	toggleMenu() {
		this.drawer = !this.drawer;
	},

	menuClose() {
		this.drawer = false;
	},

	loadDoc(json) {
		this.doc = new WDocument(json);
		this.initialize = true;
		this.docFlush();
	},

	docFlush() {
		localStorage.setItem(this.docKey, JSON.stringify({
			meta: this.meta,
			doc: this.doc,
		}));
	},

	setDoc(doc) {
		this.doc = doc;
	},

	setDocTitle(title) {
		this.doc.setTitle(title);
	},

	setTrackEdit(track) {
		this.trackEdit = track;
	},

	newSection() {
		let i = new WSection();
		this.doc.section.push(i);
		return i;
	},

	setSectionEdit(uuid) {
		this.sectionEdit = this.doc.section.find(a => a.uuid === uuid);
		this.setView("SectionEdit");
	},

	uuid() {
		return crypto.randomUUID ? crypto.randomUUID() : String(Math.random());
	},

	formatTimestamp(ts) {
		return new Intl.DateTimeFormat('it-IT', { year: 'numeric', month: 'numeric', day: 'numeric',hour: 'numeric', minute: 'numeric', hour12: false }).format(new Date(ts));
	},

	getClient(serverUUID = null) {

		if (!serverUUID && ! this.meta.serverUUID) throw "No server";

		let server = this.getServer(serverUUID || this.meta.serverUUID);
		if (!server) throw "Server not found";

		return createClient(server.url, {
			authType: AuthType.Password,
			username: server.username,
			password: server.password,
		});

	},

	/**
	 * Verifica se il doc salvato è ancora lo stesso di quello locale
	 */
	async docCheck() {
		try {
			let client = this.getClient();
			let res = await client.stat(this.meta.serverPath);
			if (res && res.lastmod !== this.meta.serverNonce) this.exception = { path: "/outdated", query: { nonce: res.lastmod } };
		} catch (e) {
			console.warn(e);
		}
	},

	docNew() {
		this.doc = new WDocument({}, this.l);
		this.meta = this.emptyMeta();
	},

	async docOpen(p) {

		try {

			let client = this.getClient(p.serverUUID);

			let json = await client.getFileContents(p.path, { format: "text" });
			let data = JSON.parse(json);

			this.meta.serverUUID = p.serverUUID;
			this.meta.serverPath = p.path;
			this.meta.serverNonce = p.serverNonce;

			this.loadDoc(data);

			this.pushMessage(this.l.doc_loaded);

		} catch (e) {

			console.error(e);
			this.pushMessage(Store.l.generic_error, "error");
			this.exception = "home";

		}

	},

	async docSave(overwrite = false) {

		if (!this.meta.serverUUID) throw "Cannot save without a server";

		let client = this.getClient();

		let res = await client.stat(this.meta.serverPath);
		if (!res) {
			alert(this.l.server_save_fail);
			return;
		}

		if (!overwrite && res.lastmod !== this.meta.serverNonce) {
			this.exception = "conflict";
			return;
		}

		this.doc.touch();
		let doc = JSON.stringify(this.doc);

		await client.putFileContents(this.meta.serverPath, doc);

		res = await client.stat(this.meta.serverPath);
		this.meta.serverNonce = res.lastmod;
		this.meta.serverDirty = false;

		this.pushMessage(this.l.doc_saved);
	},

	trackTypes() {
		return WDocument.trackTypes;
	},

	addServer(server) {
		this.settings.servers.set(server.uuid, server);
	},

	getServer(uuid) {
		return this.settings.servers.get(uuid);
	},

	delServer(uuid) {
		this.settings.servers.delete(uuid);
	},

	modServer(uuid, server) {
		server.uuid = uuid;
		this.settings.servers.set(uuid, server);
	},

	_saveSettings() {
		localStorage.setItem(this.settingsKey, JSON.stringify({
			lang: this.settings.lang,
			servers: Array.from(this.settings.servers.entries()),
		}));

		this.l = l10n[this.settings.lang];
	},
});

Store.init();

setInterval(() => Store.cron(), 1000);

export default Store;
